import React, { useMemo, useState } from 'react'
import { Alert, Box, Tab, Tabs } from '@mui/material'
import PageTitle from '../../components/PageTitle'
import { getAccess } from '../../helpers/ReduxHelpers'
import { ROLE_L1, ROLE_L2, ROLE_REQUEST } from './constants'
import NewRequestTab from './tabs/NewRequestTab'
import MyRequestsTab from './tabs/MyRequestsTab'
import PendingApprovalsTab from './tabs/PendingApprovalsTab'

/**
 * PII Data Requests (BRD Part B §7.1) — /pii-requests.
 * Tabs render by grant: New Request + My Requests need PII_REQUEST; Pending
 * Approvals needs PII_APPROVER_L1 and/or L2. A user with all grants sees all three.
 * Everything is enforced again server-side; this gating is purely presentational.
 */
export default function PiiRequests() {
  const accessList = getAccess() || []
  const canRequest = accessList.includes(ROLE_REQUEST)
  const isApprover = accessList.includes(ROLE_L1) || accessList.includes(ROLE_L2)
  const isL2 = accessList.includes(ROLE_L2)

  const tabs = useMemo(() => {
    const t = []
    if (canRequest) {
      t.push({ key: 'new', label: 'New Request' })
      t.push({ key: 'my', label: 'My Requests' })
    }
    if (isApprover) t.push({ key: 'pending', label: 'Pending Approvals' })
    return t
  }, [canRequest, isApprover])

  const [tabKey, setTabKey] = useState(tabs.length ? tabs[0].key : null)
  // bump to make My Requests reload after a create, and land the user there
  const [refreshKey, setRefreshKey] = useState(0)

  if (!tabs.length) {
    return (
      <Box sx={{ px: 3 }}>
        <PageTitle>PII Data Requests</PageTitle>
        <Alert severity="warning" sx={{ mt: 2 }}>
          You need the PII_REQUEST, PII_APPROVER_L1 or PII_APPROVER_L2 access grant to use this module.
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ px: 3, pb: 3 }}>
      <PageTitle>PII Data Requests</PageTitle>
      <Alert severity="info" sx={{ my: 1.5 }}>
        Customer contact data is delivered only inside the generated CSV, via a time-limited
        secure link. Every request, approval and download is audited.
      </Alert>
      <Tabs value={tabKey} onChange={(e, v) => setTabKey(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {tabs.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
      </Tabs>

      {tabKey === 'new' && (
        <NewRequestTab onCreated={() => { setRefreshKey((k) => k + 1); setTabKey('my') }} />
      )}
      {tabKey === 'my' && <MyRequestsTab isL2={isL2} refreshKey={refreshKey} />}
      {tabKey === 'pending' && <PendingApprovalsTab isL2={isL2} />}
    </Box>
  )
}
